import { FC, useMemo, useState } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from "@mui/material";
import {
  ArchiveOutlined as ArchiveIcon,
  UnarchiveOutlined as UnarchiveIcon,
  Public
} from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "../../../components/datagrid";
import { CountryFlag } from "../../../components/countryFlag";
import { ArchiveConfirmDialog } from "../../../components/archiveConfirmDialog";
import { CountrySummary } from "../../../api/Api";
import {
  CountryStatusFilter,
  useArchiveCountry,
  useCountryOverview,
  useRestoreCountry
} from "../../countries/dataHooks/countryQueries";
import dayjs from "dayjs";

type PendingAction = {
  mode: "archive" | "restore";
  id: string;
  name: string;
};

export const AdminCountries: FC = () => {
  const [filter, setFilter] = useState<CountryStatusFilter>("active");
  const [pending, setPending] = useState<PendingAction | null>(null);

  const { data, isLoading, isFetching } = useCountryOverview(filter);
  const archive = useArchiveCountry();
  const restore = useRestoreCountry();

  const rows = useMemo(() => data ?? [], [data]);

  const columns = useMemo<GridColDef<CountrySummary>[]>(() => {
    const base: GridColDef<CountrySummary>[] = [
      {
        field: "countryName",
        headerName: "Country",
        minWidth: 240,
        renderHeader: () => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Public fontSize="small" />
            Country
          </Box>
        ),
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CountryFlag countryName={params.value} width={20} />
            {params.value}
          </Box>
        )
      },
      {
        field: "tradingTeam",
        headerName: "Trading Team",
        width: 200
      },
      {
        field: "contactPersonTradingTeam",
        headerName: "Requester",
        width: 240
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        valueGetter: (_v, row) => (row.archived ? "Archived" : "Active")
      }
    ];

    const archiveAuditColumns: GridColDef<CountrySummary>[] = [
      {
        field: "archivedBy",
        headerName: "Archived by",
        width: 220
      },
      {
        field: "archivedAt",
        headerName: "Archived at",
        width: 220,
        valueGetter: (_v, row) =>
          row.archivedAt ? new Date(row.archivedAt) : null,
        valueFormatter: (value: Date | null) =>
          value ? dayjs(value).format("LLL") : ""
      }
    ];

    const actionsColumn: GridColDef<CountrySummary> = {
      field: "actions",
      headerName: "",
      width: 140,
      sortable: false,
      filterable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const id = params.row.id;
        if (!id) return null;
        const isArchived = params.row.archived === true;
        const name = params.row.countryName ?? "this country";
        return (
          <Tooltip title={isArchived ? "Restore" : "Archive"}>
            <Button
              size="small"
              variant="outlined"
              color={isArchived ? "primary" : "error"}
              startIcon={
                isArchived ? (
                  <UnarchiveIcon fontSize="small" />
                ) : (
                  <ArchiveIcon fontSize="small" />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                setPending({
                  mode: isArchived ? "restore" : "archive",
                  id,
                  name
                });
              }}
            >
              {isArchived ? "Restore" : "Archive"}
            </Button>
          </Tooltip>
        );
      }
    };

    return filter === "active"
      ? [...base, actionsColumn]
      : [...base, ...archiveAuditColumns, actionsColumn];
  }, [filter]);

  const handleConfirm = () => {
    if (!pending) return;
    const mutation = pending.mode === "archive" ? archive : restore;
    mutation.mutate(pending.id, {
      onSettled: () => setPending(null)
    });
  };

  const mutating = archive.isPending || restore.isPending;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_e, next: CountryStatusFilter | null) =>
            next && setFilter(next)
          }
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          loading={isLoading || isFetching || mutating}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
        />
      </Box>

      <ArchiveConfirmDialog
        open={pending !== null}
        mode={pending?.mode ?? "archive"}
        entityLabel="country"
        entityName={pending?.name ?? ""}
        onCancel={() => setPending(null)}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};
